import { useLocation, useNavigate } from 'react-router-dom'
import CustomPaper from '../../containers/Paper'
import { CommunityDTO } from '../../models/community'
import { AppRoutes } from '../../helpers/routes'
import { useContext, useState } from 'react'
import CustomModal from '../../containers/Modal'
import CommunityForm from './CommunityForm'
import MembersModal from './MembersModal'
import CustomButton from '../../components/Button'
import EditIcon from '@mui/icons-material/Edit'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import { isCurrentUser } from '../../helpers/utils'
import CommunitiesService from '../../services/CommunitiesService'
import RequestsModal from './RequestsModal'
import GroupRemoveIcon from '@mui/icons-material/GroupRemove'
import { PageContext } from '../../context/PageContext'

interface Props {
    community: CommunityDTO
    userIsMember: boolean
    singlePage?: boolean
    userIsCommunityAdmin?: boolean
    mutate?: boolean
    setMutate?: (arg: boolean) => void
    request?: boolean
}
const CommunityCard = ({
    community,
    userIsMember,
    singlePage = false,
    userIsCommunityAdmin = false,
    mutate = false,
    setMutate = () => {},
    request = true,
}: Props) => {
    const navigate = useNavigate()
    const [isEditFormOpen, setIsEditFormOpen] = useState<boolean>(false)
    const [isMembersModalOpen, setIsMembersModalOpen] = useState<boolean>(false)
    const [isRequestsModalOpen, setIsRequestsModalOpen] = useState<boolean>(false)
    const [requestSent, setRequestSent] = useState<boolean>(false)
    const { pathname } = useLocation()
    const { smallScreen } = useContext(PageContext)

    const requestPending = () =>
        !userIsMember && (community.pendingMembers.some((x) => isCurrentUser(x)) || requestSent)

    const requestJoin = () => {
        const communitiesService = new CommunitiesService()
        if (!requestPending()) {
            communitiesService.requestJoin(community.id).then((response) => {
                if (response.status !== 200) return
                setMutate(!mutate)
                if (community.isPrivate) {
                    setRequestSent(true)
                } else {
                    navigate(AppRoutes.community.replace(':communityId', community.id), {
                        state: { goBackPath: pathname },
                    })
                }
            })
        } else {
            communitiesService.removeRequest(community.id).then((response) => {
                if (response.status !== 200) return
                setRequestSent(false)
                setMutate(!mutate)
            })
        }
    }

    const getButtonText = () => {
        if (requestPending()) return 'Eliminar solicitud'
        return community.isPrivate ? 'Solicitar ingreso' : 'Unirse'
    }

    const getRequestsButton = () => {
        if (!userIsMember) {
            return (
                <CustomButton
                    startIcon={requestPending() ? <GroupRemoveIcon /> : <GroupAddIcon />}
                    secondary
                    className={
                        requestPending()
                            ? 'w-fit md:w-[180px] !bg-disabled text-[8px] md:text-base px-2 md:px-4'
                            : 'w-fit md:w-[180px] text-[8px] md:text-base px-2 md:px-4'
                    }
                    onClick={requestJoin}
                >
                    {getButtonText()}
                </CustomButton>
            )
        } else if (singlePage && userIsCommunityAdmin && community.pendingMembers.length > 0) {
            return (
                <CustomButton
                    startIcon={<GroupAddIcon />}
                    secondary
                    className="w-fit md:w-[216px] text-xs md:text-base"
                    onClick={() => setIsRequestsModalOpen(true)}
                >
                    {community.pendingMembers.length}{' '}
                    {community.pendingMembers.length === 1 ? 'solicitud' : 'solicitudes'} de ingreso
                </CustomButton>
            )
        } else return null
    }

    return (
        <CustomPaper
            className={`flex gap-2 md:gap-4 overflow-hidden p-0 ${userIsMember && !singlePage ? 'cursor-pointer' : ''}`}
            onClick={() =>
                userIsMember && !singlePage
                    ? navigate(AppRoutes.community.replace(':communityId', community.id), {
                          state: { goBackPath: pathname },
                      })
                    : {}
            }
        >
            <div
                className={`shadow-default ${singlePage ? 'h-[150px] md:h-[224px]' : 'h-[125px] md:h-[150px]'} aspect-square`}
            >
                <img
                    alt="community"
                    src={community.image ? community.image : '/communityDefaultPhoto.png'}
                    className="h-full w-full object-cover"
                />
            </div>
            <div className="p-2 md:p-5 flex flex-col justify-between w-full">
                <span className="break-words max-w-full line-clamp-1 overflow-ellipsis text-base md:text-2xl">
                    {community.name}
                </span>
                <span
                    className={`whitespace-pre-wrap break-words max-w-full ${singlePage ? 'line-clamp-5' : 'line-clamp-2'} overflow-ellipsis text-xs md:text-base`}
                    title={community.description}
                >
                    {community.description}
                </span>
                <span className="break-words line-clamp-1 text-xs md:text-base">
                    {community.isPrivate ? 'Privada' : 'Pública'} -{' '}
                    <span
                        className={
                            singlePage && userIsMember
                                ? 'cursor-pointer hover:underline text-xs md:text-base'
                                : 'text-xs md:text-base'
                        }
                        onClick={() =>
                            singlePage && userIsMember ? setIsMembersModalOpen(true) : undefined
                        }
                    >
                        {community.members.length}{' '}
                        {community.members.length === 1 ? 'miembro' : 'miembros'}
                    </span>
                </span>
            </div>
            {request ? (
                <div className="w-fit flex flex-col items-end gap-2 p-3 md:p-5">
                    {getRequestsButton()}
                    {singlePage && userIsCommunityAdmin && (
                        <CustomButton
                            startIcon={<EditIcon />}
                            secondary
                            className="w-fit"
                            onClick={() => setIsEditFormOpen(true)}
                        >
                            Editar
                        </CustomButton>
                    )}
                </div>
            ) : null}
            <CustomModal
                isOpen={isEditFormOpen}
                handleClose={() => setIsEditFormOpen(false)}
            >
                <CommunityForm
                    info={community}
                    handleClose={() => setIsEditFormOpen(false)}
                    mutate={mutate}
                    setMutate={setMutate}
                />
            </CustomModal>
            <CustomModal
                isOpen={isMembersModalOpen}
                handleClose={() => setIsMembersModalOpen(false)}
            >
                <MembersModal
                    handleClose={() => setIsMembersModalOpen(false)}
                    isCommunityAdmin={userIsCommunityAdmin}
                    community={community}
                    mutate={mutate}
                    setMutate={setMutate}
                />
            </CustomModal>
            <CustomModal
                isOpen={isRequestsModalOpen}
                handleClose={() => setIsRequestsModalOpen(false)}
            >
                <RequestsModal
                    handleClose={() => setIsRequestsModalOpen(false)}
                    community={community}
                    mutate={mutate}
                    setMutate={setMutate}
                />
            </CustomModal>
        </CustomPaper>
    )
}

export default CommunityCard
